import { useState } from "react";
import { ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";

import { cn } from "@monorepo-apps/ui/src/lib/utils";

import { ActionButton } from "@/presentation/components/delivery-tasks/action-button";
import { DeliveryTaskRowItem } from "@/presentation/components/delivery-tasks/delivery-task-row";
import { StatusDot, taskStatusToTone } from "@/presentation/components/status/status-dot";
import type { DeliveryTaskPatch } from "@/infrastructure/repositories/delivery-tasks.supabase.repository";
import type {
  DeliveryTaskRow,
  ProfileRow,
  SprintRow,
  TaskStatus,
} from "@/infrastructure/supabase/database.types";

const STATUS_ORDER: TaskStatus[] = ["in_progress", "todo", "blocked", "done"];

/**
 * A collapsible group of tasks — one sprint, or the backlog. Doubles as the
 * drop target for a task dragged out of another group.
 */
export function SprintSection({
  title,
  subtitle,
  tone,
  tasks,
  sprints,
  assignees,
  blockCodeFor,
  assigneeNameFor,
  open,
  onToggle,
  onPatchTask,
  onOpenTask,
  onDropTask,
  onAddTask,
  onEdit,
  onDelete,
  draggingId,
  onDragStartTask,
  onDragEndTask,
}: {
  title: string;
  subtitle: string | null;
  tone: "past" | "current" | "future" | "backlog";
  tasks: DeliveryTaskRow[];
  sprints: SprintRow[];
  assignees: ProfileRow[];
  blockCodeFor: (task: DeliveryTaskRow) => string | null;
  assigneeNameFor: (task: DeliveryTaskRow) => string | null;
  open: boolean;
  onToggle: () => void;
  onPatchTask: (id: string, patch: DeliveryTaskPatch) => void;
  onOpenTask: (id: string) => void;
  /** A task was dropped here — move it into this group. */
  onDropTask: (taskId: string) => void;
  /** Open the create dialog with this group already chosen as the sprint. */
  onAddTask: () => void;
  /** Edit this sprint's name and dates. Absent for the backlog, which has neither. */
  onEdit: (() => void) | null;
  /** Absent for the backlog, which can't be deleted. */
  onDelete: (() => void) | null;
  draggingId: string | null;
  onDragStartTask: (id: string) => void;
  onDragEndTask: () => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const counts: Record<TaskStatus, number> = { todo: 0, in_progress: 0, done: 0, blocked: 0 };
  for (const t of tasks) counts[t.status]++;
  const sorted = [...tasks].sort(
    (a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)
  );

  return (
    <section
      onDragOver={(e) => {
        // Preventing default is what marks this element as a valid drop target.
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={(e) => {
        // Ignore the events fired while crossing child elements.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const taskId = e.dataTransfer.getData("text/plain");
        if (taskId) onDropTask(taskId);
      }}
      // Defensive only: a cancelled drag fires `dragleave` with a null
      // relatedTarget, which the handler above already clears. `dragend` fires
      // on the drag SOURCE and bubbles only through the source's ancestors, so
      // it never reaches a destination section the pointer had moved to — this
      // is here purely for browsers that skip the cancel-time `dragleave`.
      onDragEnd={() => setDragOver(false)}
      className={cn(
        "glow-card-static p-4 transition-colors lg:p-5",
        // `outline` + `!` on the tint, because of cascade LAYERS: `.glow-card-static`
        // is declared unlayered in index.css and sets `background` and `box-shadow`,
        // while Tailwind v4 emits every utility inside `@layer utilities`. Unlayered
        // author declarations win over layered ones before specificity is even
        // considered, so a plain `bg-*` loses to its `background` and `ring-*`
        // (a box-shadow utility) loses to its `box-shadow`. `outline` is a property
        // the class does not set, so it lands; the tint needs `!` to outrank it.
        // Do NOT "simplify" this back to `ring-`/`bg-` — it renders nothing.
        dragOver &&
          "bg-[oklch(0.72_0.17_190_/_0.06)]! outline-2 -outline-offset-2 outline-[oklch(0.72_0.17_190_/_0.6)]",
        // Dim each direct child, never the <section> itself: `opacity` on the
        // section would create a stacking context, trapping the sticky header's
        // z-10 inside it so the stuck bar paints under the next sibling section.
        // Per-child is safe — the section stays a non-stacking context, so the
        // header's own z-10 still resolves against the root.
        tone === "past" &&
          !dragOver &&
          // The header is dimmed via its CHILDREN, not the header box: it is
          // sticky over scrolling rows, and a translucent `bg-card` would let
          // them show through it.
          "[&>div]:opacity-80 [&>header>*]:opacity-80 [&>p]:opacity-80 [&>table]:opacity-80"
      )}
    >
      {/* `group` sits on the header, not the toggle, so the hover-revealed edit
          and delete controls stay visible while the pointer is on them.

          Sticky: the document is the scroll container, and PipelineRibbon holds
          the top 48px (h-12, sticky z-30), so this parks just below it. The
          negative margins let the card-coloured bar span the section's padding,
          otherwise rows would scroll through the gutter beside it. */}
      <header
        className={cn(
          "bg-card group sticky top-12 z-10 -mx-4 -mt-4 flex items-center gap-3 rounded-t-xl px-4 pt-4 pb-3 lg:-mx-5 lg:-mt-5 lg:px-5 lg:pt-5",
          open && tasks.length > 0 && "border-border/40 border-b"
        )}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <ChevronRight
            className={cn(
              "text-muted-foreground size-3.5 transition-transform",
              open && "rotate-90"
            )}
            aria-hidden
          />
          <span className="font-data text-[11px] tracking-[0.16em] uppercase">{title}</span>
          {subtitle ? (
            <span className="text-muted-foreground font-data text-[10px] tracking-wider">
              {subtitle}
            </span>
          ) : null}
          {tone === "current" ? (
            <span className="font-data border-primary/50 text-primary rounded border px-1.5 py-0.5 text-[9px] tracking-[0.12em] uppercase">
              now
            </span>
          ) : null}
          <span className="text-muted-foreground ml-1 text-[11px] tabular-nums">
            {tasks.length}
          </span>
          {!open && tasks.length > 0 ? (
            <span className="text-muted-foreground flex items-center gap-2 text-[10px]">
              {STATUS_ORDER.filter((s) => counts[s] > 0).map((s) => (
                <span key={s} className="inline-flex items-center gap-1">
                  <StatusDot tone={taskStatusToTone(s)} />
                  <span className="tabular-nums">{counts[s]}</span>
                </span>
              ))}
            </span>
          ) : null}
        </button>
        {onEdit && !confirmingDelete ? (
          <button
            type="button"
            onClick={onEdit}
            className="text-muted-foreground hover:text-foreground opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100 print:hidden"
            aria-label={`Edit ${title}`}
            title="Edit name and dates"
          >
            <Pencil className="size-3.5" />
          </button>
        ) : null}
        {onDelete ? (
          confirmingDelete ? (
            <span className="flex items-center gap-2 text-[11px]">
              <span className="text-muted-foreground">Delete sprint? Tasks go to backlog.</span>
              <button
                type="button"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => setConfirmingDelete(false)}
              >
                Cancel
              </button>
              <button type="button" className="text-destructive font-medium" onClick={onDelete}>
                Delete
              </button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmingDelete(true)}
              className="text-muted-foreground hover:text-destructive opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100 print:hidden"
              aria-label={`Delete ${title}`}
              title="Delete sprint"
            >
              <Trash2 className="size-3.5" />
            </button>
          )
        ) : null}
      </header>

      {open ? (
        tasks.length === 0 ? (
          <p className="text-muted-foreground py-4 text-center text-[11px] italic">
            Nothing here — add a task below, or drag one in.
          </p>
        ) : (
          <table className="mt-3 w-full text-xs">
            <thead className="text-muted-foreground font-data text-left text-[10px] tracking-[0.18em] uppercase">
              <tr>
                <th className="w-6 print:hidden" aria-label="Drag" />
                <th className="py-2 pr-4 font-medium">Task</th>
                <th className="py-2 pr-4 font-medium">Block</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Priority</th>
                <th className="py-2 pr-4 font-medium">Assignee</th>
                <th className="py-2 pr-4 font-medium print:hidden">Sprint</th>
                <th className="py-2 pr-4 font-medium">Due</th>
                <th className="py-2 font-medium print:hidden" aria-label="Open" />
              </tr>
            </thead>
            <tbody>
              {sorted.map((task) => (
                <DeliveryTaskRowItem
                  key={task.id}
                  task={task}
                  sprints={sprints}
                  assignees={assignees}
                  blockCode={blockCodeFor(task)}
                  assigneeName={assigneeNameFor(task)}
                  onPatch={(patch) => onPatchTask(task.id, patch)}
                  onOpen={() => onOpenTask(task.id)}
                  onDragStart={() => onDragStartTask(task.id)}
                  onDragEnd={onDragEndTask}
                  isDragging={draggingId === task.id}
                />
              ))}
            </tbody>
          </table>
        )
      ) : null}

      {/* Footer action: adding a task from inside a group means the sprint is
          already decided, so the dialog opens with it pre-selected. */}
      {open ? (
        <div className="border-border/40 mt-3 border-t pt-3 print:hidden">
          <ActionButton label="Add task" icon={Plus} surface="card" onClick={onAddTask} />
        </div>
      ) : null}
    </section>
  );
}
